<template>
  <Dialog :visible="visible" header="Timetable Setup Wizard" :modal="true"
    class="p-fluid w-full max-w-2xl" @update:visible="emit('close')">

    <div class="flex items-center gap-1 mb-3">
      <div v-for="(_, i) in stepFlow" :key="i"
        class="h-1 flex-1 rounded-full transition-colors duration-300"
        :class="i <= currentStepIndex ? 'bg-primary' : 'bg-gray-200'" />
    </div>

    <div v-if="currentStepIndex > 0" class="mb-2 space-y-1">
      <div v-for="answer in completedAnswers" :key="answer.step"
        class="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-2.5 py-1 rounded-lg cursor-pointer hover:bg-gray-100"
        @click="currentStepIndex = answer.step">
        <i class="pi pi-check-circle text-primary text-xs"></i>
        <span class="font-medium">{{ answer.label }}:</span>
        <span>{{ answer.value }}</span>
        <i class="pi pi-pencil text-xs text-gray-400 ml-auto"></i>
      </div>
    </div>

    <Transition name="wizard-step" mode="out-in">
      <div :key="currentStep">

        <div v-if="currentStep === 'name'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">Let's set up your timetable!</h3>
          <p class="text-xs text-gray-500 mb-2">First, give this template a name.</p>
          <InputText v-model="wizard.name" placeholder="e.g. Standard Secondary Schedule" class="w-full"
            @keyup.enter="nextStep" />
        </div>

        <div v-else-if="currentStep === 'starting-point'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">Want to start from a common schedule?</h3>
          <p class="text-xs text-gray-500 mb-2">Pick a preset and we'll fill everything in, or build from scratch.</p>

          <div class="grid grid-cols-2 gap-2 mb-2">
            <div v-for="preset in TIMETABLE_PRESETS" :key="preset.id"
              class="border rounded-lg p-2.5 cursor-pointer transition-all hover:shadow-md"
              :class="wizard.presetId === preset.id ? 'border-primary bg-primary/10 shadow-sm' : 'border-gray-200 hover:border-gray-300'"
              @click="selectPreset(preset)">
              <div class="flex items-center gap-1.5 mb-1">
                <i :class="'pi ' + preset.icon" class="text-primary text-xs"></i>
                <span class="font-semibold text-xs text-gray-800">{{ preset.name }}</span>
              </div>
              <p class="text-[10px] text-gray-500">{{ preset.description }}</p>
            </div>
          </div>

          <div class="border-2 border-dashed rounded-lg p-2.5 cursor-pointer transition-all hover:border-gray-400 text-center"
            :class="wizard.presetId === 'custom' ? 'border-primary bg-primary/10' : 'border-gray-200'"
            @click="wizard.presetId = 'custom'">
            <i class="pi pi-cog text-gray-400 mr-1 text-xs"></i>
            <span class="font-medium text-xs text-gray-600">Build my own schedule</span>
          </div>
        </div>

        <div v-else-if="currentStep === 'start-time'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">What time does school start?</h3>
          <p class="text-xs text-gray-500 mb-2">We'll build the schedule forward from this time.</p>
          <Calendar v-model="wizardStartDate" timeOnly hourFormat="12" class="w-full" />
        </div>

        <div v-else-if="currentStep === 'period-duration'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">How long is each class period?</h3>
          <div class="space-y-1.5 mt-2">
            <div v-for="opt in PERIOD_DURATION_OPTIONS" :key="opt.value"
              class="flex items-center gap-2.5 p-2.5 border rounded-lg cursor-pointer transition-all"
              :class="wizard.periodDuration === opt.value ? 'border-primary bg-primary/10' : 'border-gray-200 hover:border-gray-300'"
              @click="wizard.periodDuration = opt.value">
              <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0"
                :class="wizard.periodDuration === opt.value ? 'border-primary' : 'border-gray-300'">
                <div v-if="wizard.periodDuration === opt.value" class="w-2 h-2 rounded-full bg-primary"></div>
              </div>
              <span class="font-medium text-sm">{{ opt.label }}</span>
              <span class="text-xs text-gray-400 ml-auto">{{ opt.hint }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="currentStep === 'periods-before-break'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">How many periods before the first break?</h3>
          <p class="text-xs text-gray-500 mb-2">Students will have this many classes, then a break.</p>
          <div class="flex items-center justify-center gap-6 mt-2">
            <Button icon="pi pi-minus" class="p-button-outlined p-button-secondary p-button-rounded p-button-sm"
              @click="wizard.periodsBeforeBreak = Math.max(1, wizard.periodsBeforeBreak - 1)" />
            <span class="text-2xl font-bold text-primary w-12 text-center">{{ wizard.periodsBeforeBreak }}</span>
            <Button icon="pi pi-plus" class="p-button-outlined p-button-secondary p-button-rounded p-button-sm"
              @click="wizard.periodsBeforeBreak = Math.min(6, wizard.periodsBeforeBreak + 1)" />
          </div>
        </div>

        <div v-else-if="currentStep === 'break-duration'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">How long is the break?</h3>
          <div class="flex gap-2 mt-2">
            <div v-for="d in [10, 15, 20, 30]" :key="d"
              class="flex-1 text-center p-2 border rounded-lg cursor-pointer transition-all text-sm"
              :class="wizard.breakDuration === d ? 'border-primary bg-primary/10 font-bold' : 'border-gray-200 hover:border-gray-300'"
              @click="wizard.breakDuration = d">
              {{ d }} min
            </div>
          </div>
        </div>

        <div v-else-if="currentStep === 'periods-after-break'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">How many periods after the break?</h3>
          <div class="flex items-center justify-center gap-6 mt-2">
            <Button icon="pi pi-minus" class="p-button-outlined p-button-secondary p-button-rounded p-button-sm"
              @click="wizard.periodsAfterBreak = Math.max(1, wizard.periodsAfterBreak - 1)" />
            <span class="text-2xl font-bold text-primary w-12 text-center">{{ wizard.periodsAfterBreak }}</span>
            <Button icon="pi pi-plus" class="p-button-outlined p-button-secondary p-button-rounded p-button-sm"
              @click="wizard.periodsAfterBreak = Math.min(8, wizard.periodsAfterBreak + 1)" />
          </div>
        </div>

        <div v-else-if="currentStep === 'lunch-break'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">Do you want a lunch break too?</h3>
          <p class="text-xs text-gray-500 mb-2">This is separate from the short break above.</p>
          <div class="flex gap-2 mb-2">
            <div class="flex-1 p-2.5 border rounded-lg cursor-pointer text-center transition-all text-sm"
              :class="wizard.hasLunch ? 'border-primary bg-primary/10 font-bold' : 'border-gray-200 hover:border-gray-300'"
              @click="wizard.hasLunch = true">
              Yes
            </div>
            <div class="flex-1 p-2.5 border rounded-lg cursor-pointer text-center transition-all text-sm"
              :class="!wizard.hasLunch ? 'border-primary bg-primary/10 font-bold' : 'border-gray-200 hover:border-gray-300'"
              @click="wizard.hasLunch = false">
              No
            </div>
          </div>
          <div v-if="wizard.hasLunch" class="space-y-2 mt-2">
            <div>
              <label class="text-xs font-medium text-gray-600 block mb-1">Lunch after which period?</label>
              <Select v-model="wizard.lunchAfterPeriod" :options="lunchPeriodOptions"
                optionLabel="label" optionValue="value" class="w-full" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-600 block mb-1">Lunch duration</label>
              <div class="flex gap-2">
                <div v-for="d in [30, 45, 60]" :key="d"
                  class="flex-1 text-center p-1.5 border rounded-lg cursor-pointer text-xs transition-all"
                  :class="wizard.lunchDuration === d ? 'border-primary bg-primary/10 font-bold' : 'border-gray-200 hover:border-gray-300'"
                  @click="wizard.lunchDuration = d">
                  {{ d }} min
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="currentStep === 'active-days'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">Which days of the week?</h3>
          <p class="text-xs text-gray-500 mb-2">Select the days this schedule applies to.</p>
          <div class="flex flex-wrap gap-1.5">
            <div v-for="day in ALL_DAYS" :key="day"
              class="px-3 py-1.5 border rounded-lg cursor-pointer transition-all text-xs"
              :class="wizard.activeDays.includes(day) ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-gray-200 text-gray-500 hover:border-gray-300'"
              @click="toggleDay(day)">
              {{ day.charAt(0) + day.slice(1).toLowerCase() }}
            </div>
          </div>
          <div class="flex gap-1.5 mt-2">
            <Button label="Mon - Fri" class="p-button-text p-button-sm text-xs"
              @click="wizard.activeDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']" />
            <Button label="Mon - Sat" class="p-button-text p-button-sm text-xs"
              @click="wizard.activeDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']" />
          </div>
        </div>

        <div v-else-if="currentStep === 'review'" class="wizard-card">
          <h3 class="text-base font-bold text-gray-800 mb-1">Here's your template!</h3>
          <p class="text-xs text-gray-500 mb-2">Review the schedule below, then click "Create Template".</p>

          <div class="bg-gray-50 rounded-lg p-2.5 mb-2">
            <div class="font-semibold text-gray-800 text-sm">{{ wizard.name }}</div>
            <div class="text-xs text-gray-500">
              {{ lessonCount }} periods &middot;
              {{ breakCount }} break(s) &middot;
              {{ wizard.activeDays.length }} days/week &middot;
              {{ previewTimeRange }}
            </div>
          </div>

          <div class="max-h-48 overflow-y-auto rounded-lg border border-gray-200">
            <table class="w-full text-xs">
              <thead class="text-[10px] text-gray-500 uppercase bg-gray-50 sticky top-0">
                <tr>
                  <th class="px-2.5 py-1.5 text-left">#</th>
                  <th class="px-2.5 py-1.5 text-left">Name</th>
                  <th class="px-2.5 py-1.5 text-left">Time</th>
                  <th class="px-2.5 py-1.5 text-left">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(slot, i) in previewSlots" :key="i"
                  class="border-b border-gray-100">
                  <td class="px-2.5 py-1.5 text-gray-400">{{ i + 1 }}</td>
                  <td class="px-2.5 py-1.5 font-medium">{{ slot.name }}</td>
                  <td class="px-2.5 py-1.5 font-mono text-[10px]">
                    {{ formatDisplayTime(slot.start_time) }} - {{ formatDisplayTime(slot.end_time) }}
                  </td>
                  <td class="px-2.5 py-1.5">
                    <span :class="slot.type === 'break' ? 'bg-yellow-100 text-yellow-800' : 'bg-primary/10 text-primary-700'"
                      class="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase">
                      {{ slot.type }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Transition>

    <template #footer>
      <div class="flex justify-between w-full">
        <Button size="small" v-if="currentStepIndex > 0" label="Back" icon="pi pi-arrow-left"
          class="p-button-text p-button-sm" @click="prevStep" />
        <div v-else></div>

        <div class="flex gap-1.5">
          <Button label="Cancel" class="p-button-text p-button-sm" @click="emit('close')" />
          <Button v-if="currentStep !== 'review'" label="Next" icon="pi pi-arrow-right" iconPos="right" size="small"
            class="!bg-primary !border-primary" @click="nextStep" :disabled="!canProceed" />
          <Button v-else label="Create Template" icon="pi pi-check" size="small"
            class="!bg-primary !border-primary" @click="createTemplate" :loading="creating" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useCreateTimetableTemplateMutation, useCreateTimetableTimeSlotMutation } from '@/types/vue-apollo-helpers';
import {
  TIMETABLE_PRESETS, PERIOD_DURATION_OPTIONS, ALL_DAYS,
  generateSlotsFromConfig, formatDisplayTime,
  type TimetablePreset, type TimetableConfig,
} from '@/constants/timetable-presets';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  close: [];
  created: [];
}>();

const { mutate: createTemplateMutation } = useCreateTimetableTemplateMutation();
const { mutate: createSlotMutation } = useCreateTimetableTimeSlotMutation();

const currentStepIndex = ref(0);
const creating = ref(false);

const wizard = reactive({
  name: '',
  presetId: '' as string,
  startTime: '08:00',
  periodDuration: 40,
  periodsBeforeBreak: 3,
  breakDuration: 15,
  periodsAfterBreak: 4,
  hasLunch: false,
  lunchAfterPeriod: 5,
  lunchDuration: 30,
  activeDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'] as string[],
});

const wizardStartDate = computed({
  get() {
    const [h, m] = wizard.startTime.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  },
  set(val: Date) {
    if (val) {
      const h = val.getHours().toString().padStart(2, '0');
      const m = val.getMinutes().toString().padStart(2, '0');
      wizard.startTime = `${h}:${m}`;
    }
  },
});

const stepFlow = computed(() => {
  const flow: string[] = ['name', 'starting-point'];
  if (wizard.presetId === 'custom') {
    flow.push('start-time', 'period-duration', 'periods-before-break',
      'break-duration', 'periods-after-break', 'lunch-break');
  }
  flow.push('active-days', 'review');
  return flow;
});

const currentStep = computed(() => stepFlow.value[currentStepIndex.value] || 'name');

const wizardConfig = computed<TimetableConfig>(() => ({
  startTime: wizard.startTime,
  periodDuration: wizard.periodDuration,
  periodsBeforeBreak: wizard.periodsBeforeBreak,
  breakDuration: wizard.breakDuration,
  periodsAfterBreak: wizard.periodsAfterBreak,
  lunchAfterPeriod: wizard.hasLunch ? wizard.lunchAfterPeriod : undefined,
  lunchDuration: wizard.hasLunch ? wizard.lunchDuration : undefined,
}));

const previewSlots = computed(() => generateSlotsFromConfig(wizardConfig.value));
const lessonCount = computed(() => previewSlots.value.filter(s => s.type === 'lesson').length);
const breakCount = computed(() => previewSlots.value.filter(s => s.type === 'break').length);
const previewTimeRange = computed(() => {
  const slots = previewSlots.value;
  if (slots.length === 0) return '';
  return `${formatDisplayTime(slots[0].start_time)} - ${formatDisplayTime(slots[slots.length - 1].end_time)}`;
});

const totalPeriods = computed(() => wizard.periodsBeforeBreak + wizard.periodsAfterBreak);

const lunchPeriodOptions = computed(() => {
  const options = [];
  for (let i = wizard.periodsBeforeBreak + 1; i < totalPeriods.value; i++) {
    options.push({ label: `After Period ${i}`, value: i });
  }
  return options;
});

const canProceed = computed(() => {
  switch (currentStep.value) {
  case 'name': return wizard.name.trim().length > 0;
  case 'starting-point': return wizard.presetId !== '';
  case 'start-time': return !!wizard.startTime;
  case 'period-duration': return wizard.periodDuration > 0;
  case 'periods-before-break': return wizard.periodsBeforeBreak > 0;
  case 'break-duration': return wizard.breakDuration > 0;
  case 'periods-after-break': return wizard.periodsAfterBreak > 0;
  case 'lunch-break': return !wizard.hasLunch || (wizard.lunchAfterPeriod > 0 && wizard.lunchDuration > 0);
  case 'active-days': return wizard.activeDays.length > 0;
  default: return true;
  }
});

const completedAnswers = computed(() => {
  const answers: Array<{ step: number; label: string; value: string }> = [];
  for (let i = 0; i < currentStepIndex.value; i++) {
    const step = stepFlow.value[i];
    switch (step) {
    case 'name':
      answers.push({ step: i, label: 'Template name', value: wizard.name });
      break;
    case 'starting-point': {
      const preset = TIMETABLE_PRESETS.find(p => p.id === wizard.presetId);
      answers.push({ step: i, label: 'Starting from', value: preset?.name || 'Custom schedule' });
      break;
    }
    case 'start-time':
      answers.push({ step: i, label: 'Start time', value: formatDisplayTime(wizard.startTime + ':00') });
      break;
    case 'period-duration':
      answers.push({ step: i, label: 'Period length', value: `${wizard.periodDuration} min` });
      break;
    case 'periods-before-break':
      answers.push({ step: i, label: 'Before break', value: `${wizard.periodsBeforeBreak} periods` });
      break;
    case 'break-duration':
      answers.push({ step: i, label: 'Break', value: `${wizard.breakDuration} min` });
      break;
    case 'periods-after-break':
      answers.push({ step: i, label: 'After break', value: `${wizard.periodsAfterBreak} periods` });
      break;
    case 'lunch-break':
      answers.push({
        step: i, label: 'Lunch',
        value: wizard.hasLunch ? `${wizard.lunchDuration} min after Period ${wizard.lunchAfterPeriod}` : 'No',
      });
      break;
    case 'active-days':
      answers.push({ step: i, label: 'Days', value: wizard.activeDays.map(d => d.slice(0, 3)).join(', ') });
      break;
    }
  }
  return answers;
});

function selectPreset(preset: TimetablePreset) {
  wizard.presetId = preset.id;
  wizard.startTime = preset.config.startTime;
  wizard.periodDuration = preset.config.periodDuration;
  wizard.periodsBeforeBreak = preset.config.periodsBeforeBreak;
  wizard.breakDuration = preset.config.breakDuration;
  wizard.periodsAfterBreak = preset.config.periodsAfterBreak;
  wizard.hasLunch = !!preset.config.lunchAfterPeriod;
  wizard.lunchAfterPeriod = preset.config.lunchAfterPeriod || 5;
  wizard.lunchDuration = preset.config.lunchDuration || 30;
  wizard.activeDays = [...preset.activeDays];
}

function toggleDay(day: string) {
  const idx = wizard.activeDays.indexOf(day);
  if (idx >= 0) {
    wizard.activeDays.splice(idx, 1);
  } else {
    wizard.activeDays.push(day);
  }
}

function nextStep() {
  if (canProceed.value && currentStepIndex.value < stepFlow.value.length - 1) {
    currentStepIndex.value++;
  }
}

function prevStep() {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
  }
}

async function createTemplate() {
  creating.value = true;
  try {
    const result = await createTemplateMutation({ input: { name: wizard.name.trim() } });
    const templateId = result?.data?.createTimetableTemplate?.id;
    if (!templateId) throw new Error('Failed to create template');

    const slots = previewSlots.value;
    for (const slot of slots) {
      await createSlotMutation({
        input: {
          timetable_template_id: templateId,
          name: slot.name,
          start_time: slot.start_time,
          end_time: slot.end_time,
          type: slot.type,
          order_index: slot.order_index,
        },
      });
    }

    emit('created');
    emit('close');
  } catch {
    // Ignore: creation failed; the finally block resets the creating flag.
  } finally {
    creating.value = false;
  }
}
</script>

<style scoped>
.wizard-step-enter-active {
  animation: wizard-slide-in 0.25s ease-out;
}
.wizard-step-leave-active {
  animation: wizard-slide-in 0.15s ease-in reverse;
}
@keyframes wizard-slide-in {
  0% { opacity: 0; transform: translateX(20px); }
  100% { opacity: 1; transform: translateX(0); }
}
</style>
